import { useCallback, useRef, useState } from 'react';
import LogicFlow from '@logicflow/core';
import { message } from 'antd';
import { nodeTypes, GroupType } from '../types/nodeTypes';
import { getNodeText } from '../utils/nodeHelpers';
import { processImportedData } from '../utils/importDataHelpers';
import { OPMApi } from '../../../api';
interface UseOPMNodeOperationsProps {
  lfRef: React.RefObject<LogicFlow | null>;
  updateGraphData: () => void;
  setNodeCounter: (counter: number | ((prev: number) => number)) => void;
  setGlobalElementOrder: (order: number | ((prev: number) => number)) => void;
  setElementAddHistory: (
    history:
      | Array<{ id: string; type: 'node' | 'edge'; timestamp: number }>
      | ((
          prev: Array<{ id: string; type: 'node' | 'edge'; timestamp: number }>
        ) => Array<{ id: string; type: 'node' | 'edge'; timestamp: number }>)
  ) => void;
  addProgramCreatedEdgeId: (edgeId: string) => void; // 添加边创建函数
  programCreatedEdgeIds: Set<string>; // 直接访问Set
  getCurrentLayerMeta?: () => { nodeIdPrefix?: string } | undefined;
  deleteNodeWithGuard?: (nodeId: string) => Promise<boolean>;
}

export const useOPMNodeOperations = ({
  lfRef,
  updateGraphData,
  setNodeCounter,
  setGlobalElementOrder,
  setElementAddHistory,
  addProgramCreatedEdgeId,
  programCreatedEdgeIds,
  getCurrentLayerMeta,
  deleteNodeWithGuard,
}: UseOPMNodeOperationsProps) => {
  // 节点菜单相关状态
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const selectedNodeIdRef = useRef<string | null>(null);
  const [nodeMenuPosition, setNodeMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [showNodeMenu, setShowNodeMenu] = useState(false);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);

  // 节点编辑相关状态
  const [showNodeEditDialog, setShowNodeEditDialog] = useState(false);
  const [editingNode, setEditingNode] = useState<any>(null);

  // 拖拽相关状态
  const [isDraggingNodeType, setIsDraggingNodeType] = useState(false);
  const [draggedNodeType, setDraggedNodeType] = useState<any>(null);

  // 导入数据弹窗状态
  const [showImportDataDialog, setShowImportDataDialog] = useState(false);
  const [importingNodeId, setImportingNodeId] = useState<string | null>(null);
  const [importingNodeName, setImportingNodeName] = useState<string>('');

  // 隐藏节点菜单
  const hideNodeMenu = useCallback(() => {
    setShowNodeMenu(false);
    setSelectedNodeId(null);
    selectedNodeIdRef.current = null;
    setNodeMenuPosition(null);
    setShowMenuDropdown(false);
  }, []);

  const closeNodeEditDialog = useCallback(() => {
    setShowNodeEditDialog(false);
    setEditingNode(null);
  }, []);

  const withLayerPrefix = useCallback(
    (rawId: string) => {
      const prefix = getCurrentLayerMeta?.()?.nodeIdPrefix || '';
      return prefix ? `${prefix}__${rawId}` : rawId;
    },
    [getCurrentLayerMeta]
  );

  const isProtectedNode = useCallback(
    (node: any) => !!node?.properties?.isSubgraphRoot,
    []
  );

  // 切换菜单下拉状态
  const toggleMenuDropdown = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setShowMenuDropdown(!showMenuDropdown);
    },
    [showMenuDropdown]
  );

  // 处理节点双击事件
  const handleNodeDoubleClick = useCallback((nodeData: any) => {
    console.log('处理节点双击:', nodeData);
    setEditingNode(nodeData);
    setShowNodeEditDialog(true);
  }, []);

  // 处理节点点击事件
  const handleNodeClick = useCallback((nodeData: any, event: any) => {
    event.stopPropagation();

    // 设置选中的节点
    setSelectedNodeId(nodeData.id);
    selectedNodeIdRef.current = nodeData.id;

    // 计算菜单位置
    const rect = event.target.getBoundingClientRect();
    const menuX = rect.right + 10; // 在节点右侧显示
    const menuY = rect.top;

    setNodeMenuPosition({ x: menuX, y: menuY });
    setShowNodeMenu(true);
    setShowMenuDropdown(false); // 确保下拉菜单初始是关闭的
  }, []);

  // 删除选中的节点
  const handleDeleteNode = useCallback(() => {
    if (!lfRef.current || !selectedNodeId) return;

    const performDelete = async () => {
      if (deleteNodeWithGuard) {
        const success = await deleteNodeWithGuard(selectedNodeId);
        if (success) {
          hideNodeMenu();
        }
        return;
      }

      console.log('删除节点:', selectedNodeId);
      const targetNode = lfRef.current?.getNodeModelById(selectedNodeId);
      if (isProtectedNode(targetNode)) {
        hideNodeMenu();
        return;
      }
      lfRef.current?.deleteNode(selectedNodeId);
      hideNodeMenu();
    };

    performDelete();

    // 注意：不需要手动调用 updateGraphData()，因为 deleteNode 会触发 node:delete 事件，
    // 事件监听器会自动调用 updateGraphData()
  }, [
    deleteNodeWithGuard,
    hideNodeMenu,
    isProtectedNode,
    lfRef,
    selectedNodeId,
  ]);

  // 编辑选中的节点
  const handleEditNode = useCallback(() => {
    if (!lfRef.current || !selectedNodeId) return;

    console.log('编辑节点:', selectedNodeId);

    // 获取当前图数据
    const graphData = lfRef.current.getGraphData() as {
      nodes: any[];
      edges: any[];
    };

    // 从图数据中找到对应的节点
    const nodeData = graphData.nodes.find(node => node.id === selectedNodeId);
    if (nodeData) {
      console.log('找到节点数据:', nodeData);
      setEditingNode(nodeData);
      setShowNodeEditDialog(true);
    } else {
      console.error('未找到节点数据:', selectedNodeId);
    }

    // 隐藏菜单
    hideNodeMenu();
  }, [lfRef, selectedNodeId, hideNodeMenu]);

  // 复制选中的节点
  const handleCopyNode = useCallback(() => {
    if (!lfRef.current || !selectedNodeId) return;

    console.log('复制节点:', selectedNodeId);

    // 获取节点数据
    const nodeData = lfRef.current.getNodeModelById(selectedNodeId);
    if (nodeData) {
      // 获取原节点的文本
      const originalText = getNodeText(nodeData);

      // 创建新节点，位置稍微偏移
      const newNodeId = withLayerPrefix(`copy_${Date.now()}`);
      const newNode = {
        id: newNodeId,
        type: nodeData.type,
        x: nodeData.x + 50,
        y: nodeData.y + 50,
        text: `${originalText}_副本`,
        properties: {
          ...nodeData.properties,
          name: `${originalText}_副本`,
          isSubgraphRoot: false,
        },
      };

      lfRef.current.addNode(newNode);

      // 记录到添加历史
      setGlobalElementOrder(prevOrder => {
        const newOrder = prevOrder + 1;
        setElementAddHistory(prev => {
          if (!prev.find(h => h.id === newNodeId)) {
            console.log(
              '记录复制节点到添加历史:',
              newNodeId,
              '顺序:',
              newOrder
            );
            return [
              ...prev,
              {
                id: newNodeId,
                type: 'node',
                timestamp: newOrder,
              },
            ];
          }
          return prev;
        });
        return newOrder;
      });

      // 更新计数器
      setNodeCounter(prev => prev + 1);
    }

    // 隐藏菜单
    hideNodeMenu();

    // 注意：不需要手动调用 updateGraphData()，因为 addNode 会触发 node:add 事件，
    // 事件监听器会自动调用 updateGraphData()
  }, [
    hideNodeMenu,
    lfRef,
    selectedNodeId,
    setElementAddHistory,
    setGlobalElementOrder,
    setNodeCounter,
    withLayerPrefix,
  ]);

  // 添加状态到实体节点
  const handleAddState = useCallback(() => {
    if (!lfRef.current || !selectedNodeId) return;

    console.log('添加状态到节点:', selectedNodeId);

    try {
      // 获取当前图数据
      const graphData = lfRef.current.getGraphData() as {
        nodes: any[];
        edges: any[];
      };

      // 找到选中的节点
      const selectedNode = graphData.nodes.find(
        node => node.id === selectedNodeId
      );
      if (!selectedNode) {
        console.warn('找不到选中的节点');
        return;
      }

      // 检查是否可以添加状态：实体节点(rect)或已转换的实体分组(dynamic-group)
      const canAddState =
        selectedNode.type === 'rect' ||
        (selectedNode.type === 'dynamic-group' &&
          selectedNode.properties?.isEntityGroup);

      console.log('选中节点信息:', {
        id: selectedNode.id,
        type: selectedNode.type,
        isEntityGroup: selectedNode.properties?.isEntityGroup,
        canAddState: canAddState,
      });

      if (!canAddState) {
        console.warn(
          '只能为实体节点或实体分组添加状态，当前节点类型:',
          selectedNode.type
        );
        return;
      }

      // 检查节点是否已经是分组节点
      const isGroup =
        selectedNode.type === 'dynamic-group' ||
        selectedNode.properties?.isGroup;

      let groupNode = selectedNode;
      const existingStates = groupNode.properties?.children || [];
      const isFirstTimeAddingStates = !isGroup && existingStates.length === 0;

      if (!isGroup) {
        // 将普通实体节点转换为动态分组节点
        console.log('将实体节点转换为动态分组节点:', selectedNodeId);

        // 为第一次添加状态时，预先调整分组大小以容纳两个默认状态
        const defaultStateCount = 2;
        const stateWidth = 70;
        const stateHeight = 30;
        const stateSpacing = 10;
        const statesPerRow = 2;
        const requiredRows = Math.ceil(defaultStateCount / statesPerRow);
        const newGroupWidth = Math.max(
          selectedNode.width || 120,
          statesPerRow * (stateWidth + stateSpacing) + 168
        );
        const newGroupHeight = Math.max(
          120,
          80 + requiredRows * (stateHeight + stateSpacing) + 20
        );

        groupNode = {
          ...selectedNode,
          type: 'dynamic-group', // 使用标准的动态分组节点类型
          x: Number(selectedNode.x) || 0, // 确保位置是有效数字
          y: Number(selectedNode.y) || 0, // 确保位置是有效数字
          properties: {
            ...selectedNode.properties,
            isGroup: true,
            isEntityGroup: true, // 标记这是一个实体分组
            originalType: 'dynamic-group',
            children: [], // 存储状态列表
            isRestrict: true, // 启用限制模式，子节点不能移出分组
            autoResize: false, // 启用自动调整大小
            collapsible: false,
            // 调整分组的尺寸以容纳状态
            width: Number(newGroupWidth) || 200, // 确保尺寸是有效数字
            height: Number(newGroupHeight) || 120, // 确保尺寸是有效数字
          },
          // 调整分组的尺寸以容纳状态
          width: Number(newGroupWidth) || 200, // 确保尺寸是有效数字
          height: Number(newGroupHeight) || 120, // 确保尺寸是有效数字
        };

        console.log('创建分组节点:', {
          id: groupNode.id,
          x: groupNode.x,
          y: groupNode.y,
          width: groupNode.width,
          height: groupNode.height,
        });
      }

      // 如果是第一次添加状态，创建两个默认状态
      const statesToCreate = isFirstTimeAddingStates ? 2 : 1;
      const stateNodes: any[] = [];
      const newStateIds: string[] = [];

      for (let i = 0; i < statesToCreate; i++) {
        // 生成状态节点ID和名称 - 基于最大已有状态序号
        const maxStateNumber = existingStates.reduce(
          (max: number, stateId: string) => {
            const match = stateId.match(/_state_(\d+)$/);
            return match ? Math.max(max, parseInt(match[1])) : max;
          },
          0
        );
        const stateCounter = maxStateNumber + i + 1;
        const stateId = `${selectedNodeId}_state_${stateCounter}`;
        const stateName = isFirstTimeAddingStates
          ? i === 0
            ? '状态1'
            : '状态2'
          : `状态${stateCounter}`;

        // 计算状态节点位置 - 在分组内部排列，基于现有状态数量
        const stateWidth = 70;
        const stateHeight = 30;
        const stateSpacing = 10; // 状态之间的间距
        const statesPerRow = 2; // 每行最多2个状态

        const stateIndex = existingStates.length + i; // 基于现有状态数量计算新状态位置
        const row = Math.floor(stateIndex / statesPerRow);
        const col = stateIndex % statesPerRow;

        // 验证分组节点的位置信息并确保数值有效
        let safeGroupX = Number(groupNode.x) || 0;
        let safeGroupY = Number(groupNode.y) || 0;
        let safeGroupHeight = Number(groupNode.height) || 120;

        // 如果位置信息不完整或无效，尝试从LogicFlow获取
        if (
          !isFinite(safeGroupX) ||
          !isFinite(safeGroupY) ||
          !isFinite(safeGroupHeight) ||
          isNaN(safeGroupX) ||
          isNaN(safeGroupY) ||
          isNaN(safeGroupHeight)
        ) {
          console.error('分组节点位置信息无效:', {
            originalX: groupNode.x,
            originalY: groupNode.y,
            originalHeight: groupNode.height,
            safeX: safeGroupX,
            safeY: safeGroupY,
            safeHeight: safeGroupHeight,
          });

          // 尝试从LogicFlow获取
          const currentGroupModel =
            lfRef.current?.getNodeModelById(selectedNodeId);
          if (currentGroupModel) {
            safeGroupX = Number(currentGroupModel.x) || 0;
            safeGroupY = Number(currentGroupModel.y) || 0;
            safeGroupHeight = Number(currentGroupModel.height) || 120;

            // 更新groupNode对象
            groupNode.x = safeGroupX;
            groupNode.y = safeGroupY;
            groupNode.height = safeGroupHeight;

            console.log('从LogicFlow获取分组位置信息:', {
              x: safeGroupX,
              y: safeGroupY,
              height: safeGroupHeight,
            });
          } else {
            // 如果还是无法获取，使用默认值
            safeGroupX = 200;
            safeGroupY = 200;
            safeGroupHeight = 120;
            console.warn('使用默认分组位置:', {
              x: safeGroupX,
              y: safeGroupY,
              height: safeGroupHeight,
            });
          }
        }

        // 计算状态在分组内的相对位置
        // 状态应该排列在分组的上方区域，为分组文字留出空间
        const groupCenterX = safeGroupX;
        const groupHeight = safeGroupHeight;
        const groupTopY = safeGroupY - groupHeight / 2;

        // 计算状态的起始位置（分组顶部下方一点，为文字留空间）
        const statesStartY = groupTopY + 40; // 为分组标题留出40px空间
        const statesStartX =
          groupCenterX -
          (statesPerRow * (stateWidth + stateSpacing) - stateSpacing) / 2;

        let stateX = statesStartX + col * (stateWidth + stateSpacing);
        let stateY = statesStartY + row * (stateHeight + stateSpacing);

        // 验证计算结果并修复NaN
        if (
          isNaN(stateX) ||
          isNaN(stateY) ||
          !isFinite(stateX) ||
          !isFinite(stateY)
        ) {
          console.error('状态节点位置计算出现NaN:', {
            groupCenterX,
            groupHeight,
            groupTopY,
            statesStartX,
            statesStartY,
            stateIndex,
            row,
            col,
            originalStateX: stateX,
            originalStateY: stateY,
          });
          // 使用安全的默认位置，基于已验证的安全坐标
          stateX = safeGroupX + (col - 0.5) * 80;
          stateY = safeGroupY - 50 + row * 40;
          console.log('使用安全默认位置:', { stateX, stateY });
        }

        // 最终安全检查，确保不是NaN
        stateX = Number(stateX) || 0;
        stateY = Number(stateY) || 0;

        // 再次验证最终结果
        if (
          isNaN(stateX) ||
          isNaN(stateY) ||
          !isFinite(stateX) ||
          !isFinite(stateY)
        ) {
          console.error('最终位置验证失败，使用绝对安全位置');
          stateX = 100 + col * 80;
          stateY = 100 + row * 50;
        }

        console.log('状态节点位置计算:', {
          groupCenterX,
          groupHeight,
          groupTopY,
          stateIndex,
          row,
          col,
          stateX,
          stateY,
        });

        // 创建状态节点（圆角矩形）
        const stateNode = {
          id: stateId,
          type: 'state', // 使用自定义的状态节点类型
          x: Number(stateX) || 0, // 确保是有效数字
          y: Number(stateY) || 0, // 确保是有效数字
          text: stateName,
          width: Number(stateWidth) || 70, // 确保是有效数字
          height: Number(stateHeight) || 30, // 确保是有效数字
          properties: {
            name: stateName,
            description: `${getNodeText(selectedNode)}的${stateName}`,
            nodeType: 'state',
            parentGroupId: selectedNodeId, // 记录父分组ID
            isState: true, // 标记这是一个状态节点
          },
        };

        console.log('创建状态节点:', {
          id: stateId,
          x: stateNode.x,
          y: stateNode.y,
          width: stateNode.width,
          height: stateNode.height,
        });

        stateNodes.push(stateNode);
        newStateIds.push(stateId);
      }

      // 更新分组的状态列表
      const updatedStates = [...existingStates, ...newStateIds];
      // 使用setProperties方法更新states属性
      if (lfRef.current) {
        const currentGroupProperties =
          lfRef.current.getNodeModelById(selectedNodeId)?.properties || {};
        lfRef.current.setProperties(selectedNodeId, {
          ...currentGroupProperties,
          states: updatedStates,
        });
      }

      // 根据最终状态数量调整分组大小
      const stateHeight = 30;
      const stateSpacing = 10;
      const statesPerRow = 2;
      const requiredRows = Math.ceil(updatedStates.length / statesPerRow);
      const newGroupHeight = Math.max(
        120,
        80 + requiredRows * (stateHeight + stateSpacing) + 20
      );
      groupNode.height = newGroupHeight;

      if (!isGroup) {
        // 如果是第一次添加状态，删除原节点和相关连线，然后重新创建分组节点和连线

        console.log(
          '将实体节点转换为分组节点（删除重建连线）:',
          selectedNodeId
        );

        // 1. 保存与原节点相关的所有连线信息
        const currentGraphData = lfRef.current.getGraphData() as {
          nodes: any[];
          edges: any[];
        };

        const relatedEdges = currentGraphData.edges.filter(
          edge =>
            edge.sourceNodeId === selectedNodeId ||
            edge.targetNodeId === selectedNodeId
        );

        console.log('保存相关连线信息:', relatedEdges.length, '条');
        console.log(
          '原连线详细信息:',
          relatedEdges.map(edge => ({
            id: edge.id,
            type: edge.type,
            text: edge.text,
            sourceNodeId: edge.sourceNodeId,
            targetNodeId: edge.targetNodeId,
            properties: edge.properties,
            startPoint: edge.startPoint,
            endPoint: edge.endPoint,
          }))
        );

        // 2. 删除原节点（这会自动删除相关连线）
        lfRef.current.deleteNode(selectedNodeId);

        // 3. 添加新的分组节点
        lfRef.current.addNode(groupNode);

        console.log('已删除原节点并添加分组节点:', selectedNodeId);

        // 4. 延迟后重新创建连线（这时连线会自动连接到新分组节点的正确边缘）
        setTimeout(() => {
          // 临时禁用边类型弹窗
          const tempDisableEdgeDialog =
            (window as any).tempDisableEdgeDialog || false;
          (window as any).tempDisableEdgeDialog = true;

          relatedEdges.forEach((edge: any, index: number) => {
            // 创建新的连线，保留类型和属性，但清除旧的位置信息让LogicFlow重新计算
            const newEdge = {
              id: edge.id, // 保持原ID
              type: edge.type, // 保留连线类型
              text: edge.text, // 保留连线文本
              properties: edge.properties, // 保留自定义属性
              sourceNodeId:
                edge.sourceNodeId === selectedNodeId
                  ? selectedNodeId
                  : edge.sourceNodeId,
              targetNodeId:
                edge.targetNodeId === selectedNodeId
                  ? selectedNodeId
                  : edge.targetNodeId,
              // 关键：不包含 startPoint, endPoint, pointsList 等具体位置信息
              // 让 LogicFlow 基于新的节点重新计算这些位置
            };

            console.log('准备重新创建连线:', {
              原边ID: edge.id,
              新边ID: newEdge.id,
              类型: newEdge.type,
              文本: newEdge.text,
              源节点: newEdge.sourceNodeId,
              目标节点: newEdge.targetNodeId,
            });

            try {
              // 生成新的边ID，避免与已删除的边ID冲突
              const newEdgeId = `${edge.id}_rebuild_${Date.now()}_${index}`;
              newEdge.id = newEdgeId;

              // 确保有类型，避免弹窗
              if (!newEdge.type) {
                newEdge.type = 'polyline'; // 设置默认类型
              }

              console.log(
                '🔗 尝试绕过弹窗机制，直接创建完整边:',
                newEdgeId,
                '类型:',
                newEdge.type
              );

              // 方案1: 先添加到程序创建边列表，然后同步添加边
              programCreatedEdgeIds.add(newEdgeId); // 直接操作Set，不用异步的setState

              // 立即添加边
              lfRef.current?.addEdge(newEdge);
              console.log(
                '✅ 重新创建连线完成:',
                newEdgeId,
                newEdge.type,
                newEdge.sourceNodeId,
                '->',
                newEdge.targetNodeId
              );
            } catch (error) {
              console.error('❌ 重新创建连线失败:', error, newEdge);
            }
          });

          // 恢复边类型弹窗功能
          setTimeout(() => {
            (window as any).tempDisableEdgeDialog = false;
            console.log('🔗 恢复边类型弹窗功能');
          }, 100);

          // 5. 确保分组节点属性正确
          setTimeout(() => {
            const newGroupNodeModel =
              lfRef.current?.getNodeModelById(selectedNodeId);
            if (newGroupNodeModel) {
              newGroupNodeModel.setAttributes();
              if (newGroupNodeModel.setIsShowAnchor) {
                newGroupNodeModel.setIsShowAnchor(true);
              }
              console.log('🎯 节点转换和连线重建完成:', selectedNodeId);
            }
          }, 50);
        }, 100); // 延迟确保分组节点完全添加后再创建连线

        console.log('节点转换开始（连线将重新创建）:', selectedNodeId);
      } else {
        // 如果已经是分组，重新计算分组节点的尺寸以容纳新状态
        const groupNodeModel = lfRef.current.getNodeModelById(selectedNodeId);
        if (groupNodeModel) {
          // 计算新的分组尺寸，考虑现有状态和新增状态
          const totalStatesAfterAdd = existingStates.length + statesToCreate;
          const stateWidth = 70;
          const stateHeight = 30;
          const stateSpacing = 10;
          const statesPerRow = 2;
          const requiredRows = Math.ceil(totalStatesAfterAdd / statesPerRow);

          const newGroupWidth = Math.max(
            Number(groupNodeModel.width) || 200,
            statesPerRow * (stateWidth + stateSpacing) + 40
          );
          const newGroupHeight =
            Math.max(
              Number(groupNodeModel.height) || 120,
              80 + requiredRows * (stateHeight + stateSpacing) + 20
            ) + 20;

          // 更新groupNode对象以包含当前的位置信息和新计算的尺寸
          groupNode.x = groupNodeModel.x;
          groupNode.y = groupNodeModel.y;
          groupNode.width = newGroupWidth;
          groupNode.height = newGroupHeight;
          groupNode.properties.width = newGroupWidth;
          groupNode.properties.height = newGroupHeight;

          // 更新模型
          groupNodeModel.width = groupNode.width;
          groupNodeModel.height = groupNode.height;
          groupNodeModel.properties.width = groupNode.width;
          groupNodeModel.properties.height = groupNode.height;

          // 使用setProperties方法更新属性
          if (lfRef.current) {
            lfRef.current.setProperties(selectedNodeId, groupNode.properties);
          }

          // 使用LogicFlow原生方法重新计算节点尺寸和锚点位置
          groupNodeModel.setAttributes();
          // 确保分组节点显示锚点
          if (groupNodeModel.setIsShowAnchor) {
            groupNodeModel.setIsShowAnchor(true);
          }

          console.log('更新已有分组节点:', {
            id: selectedNodeId,
            x: groupNode.x,
            y: groupNode.y,
            width: groupNode.width,
            height: groupNode.height,
            totalStates: totalStatesAfterAdd,
            requiredRows: requiredRows,
          });
        }
      }

      // 获取分组模型，按照LogicFlow官方文档的方式创建子节点
      const groupNodeModel = lfRef.current?.getNodeModelById(selectedNodeId);

      if (groupNodeModel) {
        // 添加所有状态节点到分组中
        stateNodes.forEach(stateNode => {
          // 先添加节点到画布
          const addedNode = lfRef.current?.addNode(stateNode);

          if (addedNode) {
            // 使用LogicFlow官方API建立分组关系
            if (
              groupNodeModel.addChild &&
              typeof groupNodeModel.addChild === 'function'
            ) {
              // 延迟执行，确保节点完全添加后再建立关系
              setTimeout(() => {
                try {
                  groupNodeModel.addChild(stateNode.id);
                  console.log(
                    '已将状态节点添加到分组:',
                    stateNode.id,
                    '->',
                    selectedNodeId
                  );
                } catch (error) {
                  console.warn('使用addChild建立分组关系失败:', error);
                  // 备用方案：使用setProperties设置父子关系属性
                  const stateNodeModel = lfRef.current?.getNodeModelById(
                    stateNode.id
                  );
                  if (stateNodeModel && lfRef.current) {
                    const currentProperties = stateNodeModel.properties || {};
                    lfRef.current.setProperties(stateNode.id, {
                      ...currentProperties,
                      parentId: selectedNodeId,
                    });
                    console.log('使用备用方案设置父子关系:', stateNode.id);
                  }
                }
              }, 10);
            } else {
              console.warn('分组节点模型不支持addChild方法，使用备用方案:', {
                hasAddChild: !!groupNodeModel.addChild,
                selectedNodeId,
              });

              // 备用方案：使用setProperties设置父子关系属性
              setTimeout(() => {
                const stateNodeModel = lfRef.current?.getNodeModelById(
                  stateNode.id
                );
                if (stateNodeModel && lfRef.current) {
                  const currentProperties = stateNodeModel.properties || {};
                  lfRef.current.setProperties(stateNode.id, {
                    ...currentProperties,
                    parentId: selectedNodeId,
                  });
                  console.log('使用备用方案设置父子关系:', stateNode.id);
                }
              }, 10);
            }
          }
        });
      } else {
        console.error(
          '无法获取分组节点模型，直接添加状态节点:',
          selectedNodeId
        );
        // 如果无法获取分组模型，直接添加节点
        stateNodes.forEach(stateNode => {
          lfRef.current?.addNode(stateNode);
        });
      }

      // 记录状态节点到添加历史
      setGlobalElementOrder(prevOrder => {
        let currentOrder = prevOrder;
        const newHistoryEntries: Array<{
          id: string;
          type: 'node';
          timestamp: number;
        }> = [];

        stateNodes.forEach(stateNode => {
          currentOrder += 1;
          newHistoryEntries.push({
            id: stateNode.id,
            type: 'node',
            timestamp: currentOrder,
          });
          console.log(
            '记录状态节点到添加历史:',
            stateNode.id,
            '顺序:',
            currentOrder
          );
        });

        // 批量更新历史记录
        setElementAddHistory(prev => {
          const filteredEntries = newHistoryEntries.filter(
            entry => !prev.find(h => h.id === entry.id)
          );
          return [...prev, ...filteredEntries];
        });

        return currentOrder;
      });

      console.log(
        `状态添加完成: 添加了${statesToCreate}个状态到节点:`,
        selectedNodeId
      );
    } catch (error) {
      console.error('添加状态时出错:', error);
    }

    // 隐藏菜单
    hideNodeMenu();
  }, [
    lfRef,
    selectedNodeId,
    setGlobalElementOrder,
    setElementAddHistory,
    hideNodeMenu,
    getNodeText,
  ]);

  // 删除状态节点
  const handleDeleteState = useCallback(() => {
    if (!lfRef.current || !selectedNodeId) return;

    console.log('删除状态节点:', selectedNodeId);

    try {
      // 获取当前图数据
      const graphData = lfRef.current.getGraphData() as {
        nodes: any[];
        edges: any[];
      };

      // 找到要删除的状态节点
      const stateNode = graphData.nodes.find(
        node => node.id === selectedNodeId
      );
      if (
        !stateNode ||
        (!stateNode.properties?.isState && stateNode.type !== 'state')
      ) {
        console.warn('只能删除状态节点');
        return;
      }

      const parentGroupId = stateNode.properties?.parentGroupId;
      if (!parentGroupId) {
        console.warn('状态节点没有父分组ID');
        return;
      }

      // 找到父分组节点
      const parentGroup = graphData.nodes.find(
        node => node.id === parentGroupId
      );
      if (!parentGroup) {
        console.warn('找不到父分组节点');
        return;
      }

      // 从父分组的状态列表中移除该状态
      const updatedStates = (parentGroup.properties?.children || []).filter(
        (stateId: string) => stateId !== selectedNodeId
      );

      // 更新父分组节点
      const updatedParentGroup = {
        ...parentGroup,
        properties: {
          ...parentGroup.properties,
          children: updatedStates,
          isRestrict: true, // 保持限制模式
          autoResize: false, // 保持自动调整大小
        },
      };

      // 如果没有状态了，将实体分组节点转换回普通实体节点
      if (updatedStates.length === 0) {
        console.log('没有状态了，将实体分组转换回普通实体节点');
        updatedParentGroup.type = 'rect';
        // 使用setProperties方法更新属性
        if (lfRef.current) {
          const currentProperties =
            lfRef.current.getNodeModelById(parentGroupId)?.properties || {};
          lfRef.current.setProperties(parentGroupId, {
            ...currentProperties,
            isGroup: false,
            isEntityGroup: false,
          });
        }
        // 恢复原始尺寸
        updatedParentGroup.width = 120;
        updatedParentGroup.height = 80;
      } else {
        // 重新计算分组大小
        const stateHeight = 30;
        const stateSpacing = 10;
        const statesPerRow = 2;
        const requiredRows = Math.ceil(updatedStates.length / statesPerRow);
        const newGroupHeight = Math.max(
          120,
          80 + requiredRows * (stateHeight + stateSpacing) + 20
        );
        updatedParentGroup.height = newGroupHeight;
      }

      // 重新排列剩余的状态节点位置
      const updatedNodes = graphData.nodes
        .filter(node => node.id !== selectedNodeId) // 移除被删除的状态节点
        .map(node => {
          if (node.id === parentGroupId) {
            return updatedParentGroup; // 更新父分组
          }

          // 重新定位剩余的状态节点
          if (updatedStates.includes(node.id) && node.properties?.isState) {
            const stateIndex = updatedStates.indexOf(node.id);
            const stateWidth = 70;
            const stateHeight = 30;
            const stateSpacing = 10;
            const statesPerRow = 2;

            const row = Math.floor(stateIndex / statesPerRow);
            const col = stateIndex % statesPerRow;

            const stateX =
              updatedParentGroup.x -
              (stateWidth + stateSpacing) / 2 +
              col * (stateWidth + stateSpacing);
            const stateY =
              updatedParentGroup.y - 10 + row * (stateHeight + stateSpacing);

            return {
              ...node,
              x: stateX,
              y: stateY,
            };
          }

          return node;
        });

      // 重新渲染图形
      const newGraphData = {
        nodes: updatedNodes,
        edges: graphData.edges,
      };

      lfRef.current.render(newGraphData);

      // 使用LogicFlow原生方法重新计算父节点的锚点位置（特别是当分组变回实体时）
      setTimeout(() => {
        const parentNodeModel = lfRef.current?.getNodeModelById(parentGroupId);
        if (parentNodeModel) {
          parentNodeModel.setAttributes();
          // 如果是分组节点，确保显示锚点
          if (parentNodeModel.setIsShowAnchor) {
            parentNodeModel.setIsShowAnchor(true);
          }
          console.log('🎯 重新计算父节点锚点位置:', parentGroupId);
        }
      }, 50);

      console.log('状态删除完成:', selectedNodeId);

      // 更新图数据
      setTimeout(() => {
        updateGraphData();
      }, 100);
    } catch (error) {
      console.error('删除状态时出错:', error);
    }

    // 隐藏菜单
    hideNodeMenu();
  }, [lfRef, selectedNodeId, hideNodeMenu, updateGraphData]);

  // 添加值到实体节点（与添加状态逻辑类似，但互斥）
  const handleAddValue = useCallback(() => {
    if (!lfRef.current || !selectedNodeId) return;

    console.log('添加值到节点:', selectedNodeId);

    try {
      const graphData = lfRef.current.getGraphData() as {
        nodes: any[];
        edges: any[];
      };

      const selectedNode = graphData.nodes.find(
        node => node.id === selectedNodeId
      );
      if (!selectedNode) {
        console.warn('找不到选中的节点');
        return;
      }

      // 检查互斥：如果已经是状态分组，不能添加值
      const nodeGroupType = selectedNode.properties?.groupType;
      const isStateGroup =
        selectedNode.properties?.isEntityGroup &&
        (!nodeGroupType || nodeGroupType === GroupType.STATE);
      if (isStateGroup) {
        console.warn('已添加状态的分组不能添加值');
        return;
      }

      const canAddValue =
        selectedNode.type === 'rect' ||
        (selectedNode.type === 'dynamic-group' &&
          nodeGroupType === GroupType.VALUE);

      if (!canAddValue) {
        console.warn('只能为实体节点或值分组添加值');
        return;
      }

      const isGroup =
        selectedNode.type === 'dynamic-group' ||
        selectedNode.properties?.isGroup;

      let groupNode = selectedNode;
      const existingValues = groupNode.properties?.children || [];
      const isFirstTimeAddingValues = !isGroup && existingValues.length === 0;

      if (!isGroup) {
        const defaultValueCount = 2;
        const valueWidth = 70;
        const valueHeight = 30;
        const valueSpacing = 10;
        const valuesPerRow = 2;
        const requiredRows = Math.ceil(defaultValueCount / valuesPerRow);
        const newGroupWidth = Math.max(
          selectedNode.width || 120,
          valuesPerRow * (valueWidth + valueSpacing) + 168
        );
        const newGroupHeight = Math.max(
          120,
          80 + requiredRows * (valueHeight + valueSpacing) + 20
        );

        groupNode = {
          ...selectedNode,
          type: 'dynamic-group',
          x: Number(selectedNode.x) || 0,
          y: Number(selectedNode.y) || 0,
          properties: {
            ...selectedNode.properties,
            isGroup: true,
            isEntityGroup: true,
            groupType: GroupType.VALUE,
            originalType: 'dynamic-group',
            children: [],
            isRestrict: true,
            autoResize: false,
            collapsible: false,
            width: Number(newGroupWidth) || 200,
            height: Number(newGroupHeight) || 120,
          },
          width: Number(newGroupWidth) || 200,
          height: Number(newGroupHeight) || 120,
        };
      }

      const valuesToCreate = isFirstTimeAddingValues ? 2 : 1;
      const valueNodes: any[] = [];
      const newValueIds: string[] = [];

      for (let i = 0; i < valuesToCreate; i++) {
        const maxValueNumber = existingValues.reduce(
          (max: number, valueId: string) => {
            const match = valueId.match(/_value_(\d+)$/);
            return match ? Math.max(max, parseInt(match[1])) : max;
          },
          0
        );
        const valueCounter = maxValueNumber + i + 1;
        const valueId = `${selectedNodeId}_value_${valueCounter}`;
        const valueName = isFirstTimeAddingValues
          ? i === 0
            ? '值1'
            : '值2'
          : `值${valueCounter}`;

        const valueWidth = 70;
        const valueHeight = 30;
        const valueSpacing = 10;
        const valuesPerRow = 2;

        const valueIndex = existingValues.length + i;
        const row = Math.floor(valueIndex / valuesPerRow);
        const col = valueIndex % valuesPerRow;

        let safeGroupX = Number(groupNode.x) || 0;
        let safeGroupY = Number(groupNode.y) || 0;
        let safeGroupHeight = Number(groupNode.height) || 120;

        if (
          !isFinite(safeGroupX) ||
          !isFinite(safeGroupY) ||
          !isFinite(safeGroupHeight)
        ) {
          const currentGroupModel =
            lfRef.current?.getNodeModelById(selectedNodeId);
          if (currentGroupModel) {
            safeGroupX = Number(currentGroupModel.x) || 200;
            safeGroupY = Number(currentGroupModel.y) || 200;
            safeGroupHeight = Number(currentGroupModel.height) || 120;
            groupNode.x = safeGroupX;
            groupNode.y = safeGroupY;
            groupNode.height = safeGroupHeight;
          }
        }

        const groupCenterX = safeGroupX;
        const groupHeight = safeGroupHeight;
        const groupTopY = safeGroupY - groupHeight / 2;
        const valuesStartY = groupTopY + 40;
        const valuesStartX =
          groupCenterX -
          (valuesPerRow * (valueWidth + valueSpacing) - valueSpacing) / 2;

        let valueX = valuesStartX + col * (valueWidth + valueSpacing);
        let valueY = valuesStartY + row * (valueHeight + valueSpacing);

        if (isNaN(valueX) || isNaN(valueY)) {
          valueX = safeGroupX + (col - 0.5) * 80;
          valueY = safeGroupY - 50 + row * 40;
        }

        const valueNode = {
          id: valueId,
          type: 'value',
          x: Number(valueX) || 0,
          y: Number(valueY) || 0,
          text: valueName,
          width: Number(valueWidth) || 70,
          height: Number(valueHeight) || 30,
          properties: {
            name: valueName,
            description: `${getNodeText(selectedNode)}的${valueName}`,
            nodeType: 'value',
            parentGroupId: selectedNodeId,
            isValue: true,
          },
        };

        valueNodes.push(valueNode);
        newValueIds.push(valueId);
      }

      const updatedValues = [...existingValues, ...newValueIds];
      if (lfRef.current) {
        const currentGroupProperties =
          lfRef.current.getNodeModelById(selectedNodeId)?.properties || {};
        lfRef.current.setProperties(selectedNodeId, {
          ...currentGroupProperties,
          values: updatedValues,
        });
      }

      const valueHeight = 30;
      const valueSpacing = 10;
      const valuesPerRow = 2;
      const requiredRows = Math.ceil(updatedValues.length / valuesPerRow);
      const newGroupHeight = Math.max(
        120,
        80 + requiredRows * (valueHeight + valueSpacing) + 20
      );
      groupNode.height = newGroupHeight;

      if (!isGroup) {
        const currentGraphData = lfRef.current.getGraphData() as {
          nodes: any[];
          edges: any[];
        };
        const relatedEdges = currentGraphData.edges.filter(
          edge =>
            edge.sourceNodeId === selectedNodeId ||
            edge.targetNodeId === selectedNodeId
        );

        lfRef.current.deleteNode(selectedNodeId);
        lfRef.current.addNode(groupNode);

        setTimeout(() => {
          (window as any).tempDisableEdgeDialog = true;
          relatedEdges.forEach((edge: any, index: number) => {
            const newEdgeId = `${edge.id}_rebuild_${Date.now()}_${index}`;
            const newEdge = {
              id: newEdgeId,
              type: edge.type || 'polyline',
              text: edge.text,
              properties: edge.properties,
              sourceNodeId: edge.sourceNodeId,
              targetNodeId: edge.targetNodeId,
            };
            try {
              programCreatedEdgeIds.add(newEdgeId);
              lfRef.current?.addEdge(newEdge);
            } catch (error) {
              console.error('重新创建连线失败:', error);
            }
          });
          setTimeout(() => {
            (window as any).tempDisableEdgeDialog = false;
          }, 100);
          setTimeout(() => {
            const newGroupNodeModel =
              lfRef.current?.getNodeModelById(selectedNodeId);
            if (newGroupNodeModel) {
              newGroupNodeModel.setAttributes();
              if (newGroupNodeModel.setIsShowAnchor)
                newGroupNodeModel.setIsShowAnchor(true);
            }
          }, 50);
        }, 100);
      } else {
        const groupNodeModel = lfRef.current.getNodeModelById(selectedNodeId);
        if (groupNodeModel) {
          const totalValuesAfterAdd = existingValues.length + valuesToCreate;
          const requiredRows = Math.ceil(totalValuesAfterAdd / 2);
          const newGroupWidth = Math.max(
            Number(groupNodeModel.width) || 200,
            2 * 80 + 40
          );
          const newGroupHeight =
            Math.max(
              Number(groupNodeModel.height) || 120,
              80 + requiredRows * 40 + 20
            ) + 20;

          groupNode.x = groupNodeModel.x;
          groupNode.y = groupNodeModel.y;
          groupNode.width = newGroupWidth;
          groupNode.height = newGroupHeight;
          groupNodeModel.width = newGroupWidth;
          groupNodeModel.height = newGroupHeight;
          if (lfRef.current)
            lfRef.current.setProperties(selectedNodeId, groupNode.properties);
          groupNodeModel.setAttributes();
          if (groupNodeModel.setIsShowAnchor)
            groupNodeModel.setIsShowAnchor(true);
        }
      }

      const groupNodeModel = lfRef.current?.getNodeModelById(selectedNodeId);
      if (groupNodeModel) {
        valueNodes.forEach(valueNode => {
          const addedNode = lfRef.current?.addNode(valueNode);
          if (addedNode && groupNodeModel.addChild) {
            setTimeout(() => {
              try {
                groupNodeModel.addChild(valueNode.id);
              } catch (error) {
                const valueNodeModel = lfRef.current?.getNodeModelById(
                  valueNode.id
                );
                if (valueNodeModel && lfRef.current) {
                  lfRef.current.setProperties(valueNode.id, {
                    ...valueNodeModel.properties,
                    parentId: selectedNodeId,
                  });
                }
              }
            }, 10);
          }
        });
      } else {
        valueNodes.forEach(valueNode => lfRef.current?.addNode(valueNode));
      }

      setGlobalElementOrder(prevOrder => {
        let currentOrder = prevOrder;
        const newHistoryEntries: Array<{
          id: string;
          type: 'node';
          timestamp: number;
        }> = [];
        valueNodes.forEach(valueNode => {
          currentOrder += 1;
          newHistoryEntries.push({
            id: valueNode.id,
            type: 'node',
            timestamp: currentOrder,
          });
        });
        setElementAddHistory(prev => [
          ...prev,
          ...newHistoryEntries.filter(e => !prev.find(h => h.id === e.id)),
        ]);
        return currentOrder;
      });

      console.log(
        `值添加完成: 添加了${valuesToCreate}个值到节点:`,
        selectedNodeId
      );
    } catch (error) {
      console.error('添加值时出错:', error);
    }

    hideNodeMenu();
  }, [
    lfRef,
    selectedNodeId,
    setGlobalElementOrder,
    setElementAddHistory,
    hideNodeMenu,
    programCreatedEdgeIds,
  ]);

  // 删除值节点
  const handleDeleteValue = useCallback(() => {
    if (!lfRef.current || !selectedNodeId) return;

    try {
      const graphData = lfRef.current.getGraphData() as {
        nodes: any[];
        edges: any[];
      };
      const valueNode = graphData.nodes.find(
        node => node.id === selectedNodeId
      );

      if (
        !valueNode ||
        (!valueNode.properties?.isValue && valueNode.type !== 'value')
      ) {
        console.warn('只能删除值节点');
        return;
      }

      const parentGroupId = valueNode.properties?.parentGroupId;
      if (!parentGroupId) return;

      const parentGroup = graphData.nodes.find(
        node => node.id === parentGroupId
      );
      if (!parentGroup) return;

      const updatedValues = (parentGroup.properties?.children || []).filter(
        (valueId: string) => valueId !== selectedNodeId
      );

      const updatedParentGroup = {
        ...parentGroup,
        properties: {
          ...parentGroup.properties,
          children: updatedValues,
          isRestrict: true,
          autoResize: false,
        },
      };

      if (updatedValues.length === 0) {
        updatedParentGroup.type = 'rect';
        if (lfRef.current) {
          lfRef.current.setProperties(parentGroupId, {
            ...lfRef.current.getNodeModelById(parentGroupId)?.properties,
            isGroup: false,
            isEntityGroup: false,
            groupType: undefined,
          });
        }
        updatedParentGroup.width = 120;
        updatedParentGroup.height = 80;
      } else {
        const requiredRows = Math.ceil(updatedValues.length / 2);
        updatedParentGroup.height = Math.max(120, 80 + requiredRows * 40 + 20);
      }

      const updatedNodes = graphData.nodes
        .filter(node => node.id !== selectedNodeId)
        .map(node => {
          if (node.id === parentGroupId) return updatedParentGroup;
          if (updatedValues.includes(node.id) && node.properties?.isValue) {
            const valueIndex = updatedValues.indexOf(node.id);
            const row = Math.floor(valueIndex / 2);
            const col = valueIndex % 2;
            return {
              ...node,
              x: updatedParentGroup.x - 40 + col * 80,
              y: updatedParentGroup.y - 10 + row * 40,
            };
          }
          return node;
        });

      lfRef.current.render({ nodes: updatedNodes, edges: graphData.edges });

      setTimeout(() => {
        const parentNodeModel = lfRef.current?.getNodeModelById(parentGroupId);
        if (parentNodeModel) {
          parentNodeModel.setAttributes();
          if (parentNodeModel.setIsShowAnchor)
            parentNodeModel.setIsShowAnchor(true);
        }
      }, 50);

      setTimeout(() => updateGraphData(), 100);
    } catch (error) {
      console.error('删除值时出错:', error);
    }

    hideNodeMenu();
  }, [lfRef, selectedNodeId, hideNodeMenu, updateGraphData]);

  // 确认节点编辑 - 只允许编辑名称和描述
  const handleNodeEditConfirm = useCallback(
    (updatedNodeData: any) => {
      if (!lfRef.current || !updatedNodeData) return;

      console.log('确认节点编辑:', updatedNodeData);

      try {
        // 只更新节点的名称和描述属性
        const { id, text, properties } = updatedNodeData;

        // 构建新的属性对象，只包含允许编辑的字段
        const newProperties = {
          // 保留原有的所有属性
          ...lfRef.current.getNodeModelById(id)?.properties,
          // 只更新名称和描述
          name: text,
          description: properties?.description || '',
        };

        // 使用 LogicFlow 的 setProperties 方法更新节点属性
        lfRef.current.setProperties(id, newProperties);

        // 更新节点文本（显示名称）
        if (text !== undefined) {
          lfRef.current.updateText(id, text);
        }

        console.log('节点编辑完成（仅更新名称和描述）:', id);

        // 延迟更新图数据缓存
        setTimeout(() => {
          updateGraphData();
        }, 100);

        console.log('节点更新成功');
      } catch (error) {
        console.error('节点更新失败:', error);
      }

      // 重置状态
      setShowNodeEditDialog(false);
      setEditingNode(null);
    },
    [lfRef, updateGraphData]
  );

  // 取消节点编辑
  const handleNodeEditCancel = useCallback(() => {
    console.log('取消节点编辑');
    setShowNodeEditDialog(false);
    setEditingNode(null);
  }, []);

  // 拖拽处理函数
  const handleNodeTypeDragStart = useCallback(
    (e: React.DragEvent, nodeType: any) => {
      console.log('开始拖拽节点类型:', nodeType);
      setIsDraggingNodeType(true);
      setDraggedNodeType(nodeType);

      // 设置拖拽数据 - 只传递需要的属性，避免序列化React组件导致的循环引用
      if (e.dataTransfer) {
        const dragData = {
          type: nodeType.type,
          name: nodeType.name,
          color: nodeType.color,
        };
        e.dataTransfer.setData('application/json', JSON.stringify(dragData));
        e.dataTransfer.effectAllowed = 'copy';
      }
    },
    []
  );

  const handleNodeTypeDragEnd = useCallback(() => {
    console.log('拖拽结束');
    setIsDraggingNodeType(false);
    setDraggedNodeType(null);
  }, []);

  // 打开导入数据弹窗
  const handleOpenImportDataDialog = useCallback(() => {
    if (!lfRef.current || !selectedNodeId) return;

    const graphData = lfRef.current.getGraphData() as {
      nodes: any[];
      edges: any[];
    };
    const selectedNode = graphData.nodes.find(
      node => node.id === selectedNodeId
    );

    if (!selectedNode) {
      console.warn('找不到选中的节点');
      return;
    }

    // 获取节点名称
    const nodeName =
      getNodeText(selectedNode) || selectedNode.properties?.name || '';

    setImportingNodeId(selectedNodeId);
    setImportingNodeName(nodeName);
    setShowImportDataDialog(true);
    hideNodeMenu();
  }, [lfRef, selectedNodeId, hideNodeMenu]);

  // 关闭导入数据弹窗
  const handleCloseImportDataDialog = useCallback(() => {
    setShowImportDataDialog(false);
    setImportingNodeId(null);
    setImportingNodeName('');
  }, []);

  // 处理导入数据确认
  const handleImportDataConfirm = useCallback(
    async (file: File) => {
      if (!lfRef.current || !importingNodeId) {
        throw new Error('无法获取画布实例或目标节点');
      }

      console.log('📤 开始导入数据:', {
        nodeId: importingNodeId,
        fileName: file.name,
        fileSize: file.size,
      });

      try {
        // 调用封装的 API（已设置 5 分钟超时）
        const importedData = await OPMApi.importData(file, importingNodeId);

        if (!importedData || !importedData.nodes) {
          throw new Error('返回数据格式错误');
        }

        // 获取现有节点数据
        const currentGraphData = lfRef.current.getGraphData() as {
          nodes: any[];
          edges: any[];
        };

        // 处理导入的数据，生成坐标
        const { nodes: newNodes, edges: newEdges } = processImportedData(
          importedData,
          importingNodeId,
          currentGraphData.nodes
        );

        console.log('📊 处理后的数据:', {
          nodesCount: newNodes.length,
          edgesCount: newEdges.length,
        });

        // 标记这些边是程序创建的，避免触发边类型弹窗
        newEdges.forEach(edge => {
          programCreatedEdgeIds.add(edge.id);
        });

        // 使用 render 方法一次性渲染所有节点和边，避免逐个添加触发 N 次 updateGraphData
        const mergedGraphData = {
          nodes: [...currentGraphData.nodes, ...newNodes],
          edges: [...currentGraphData.edges, ...newEdges],
        };

        console.log('🔄 批量渲染图数据:', {
          totalNodes: mergedGraphData.nodes.length,
          totalEdges: mergedGraphData.edges.length,
        });

        lfRef.current.render(mergedGraphData);

        // 渲染完成后更新一次图数据
        setTimeout(() => {
          updateGraphData();

          // 记录到添加历史
          setGlobalElementOrder(prevOrder => {
            let currentOrder = prevOrder;
            const newHistoryEntries: Array<{
              id: string;
              type: 'node' | 'edge';
              timestamp: number;
            }> = [];

            newNodes.forEach(node => {
              currentOrder += 1;
              newHistoryEntries.push({
                id: node.id,
                type: 'node',
                timestamp: currentOrder,
              });
            });

            newEdges.forEach(edge => {
              currentOrder += 1;
              newHistoryEntries.push({
                id: edge.id,
                type: 'edge',
                timestamp: currentOrder,
              });
            });

            setElementAddHistory(prev => [...prev, ...newHistoryEntries]);
            return currentOrder;
          });

          console.log('✅ 数据导入完成');
        }, 100);

        // 关闭弹窗
        handleCloseImportDataDialog();
        message.success(
          `成功导入 ${newNodes.length} 个节点和 ${newEdges.length} 条边`
        );
      } catch (error: any) {
        console.error('❌ 导入数据失败:', error);
        throw new Error(error?.message || '导入失败，请重试');
      }
    },
    [
      lfRef,
      importingNodeId,
      programCreatedEdgeIds,
      updateGraphData,
      setGlobalElementOrder,
      setElementAddHistory,
      handleCloseImportDataDialog,
    ]
  );

  return {
    // 状态
    selectedNodeId,
    selectedNodeIdRef,
    nodeMenuPosition,
    showNodeMenu,
    showMenuDropdown,
    showNodeEditDialog,
    editingNode,
    isDraggingNodeType,
    draggedNodeType,

    // 导入数据弹窗状态
    showImportDataDialog,
    importingNodeId,
    importingNodeName,

    // 节点菜单操作
    hideNodeMenu,
    toggleMenuDropdown,
    handleNodeClick,

    // 节点编辑操作
    handleNodeDoubleClick,
    handleNodeEditConfirm,
    handleNodeEditCancel,

    // 节点操作
    handleDeleteNode,
    handleEditNode,
    handleCopyNode,
    handleAddState,
    handleDeleteState,
    handleAddValue,
    handleDeleteValue,

    // 导入数据操作
    handleOpenImportDataDialog,
    handleCloseImportDataDialog,
    handleImportDataConfirm,

    // 拖拽操作
    handleNodeTypeDragStart,
    handleNodeTypeDragEnd,
    closeNodeEditDialog,
  };
};
